import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./Reception.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
  FaUserCheck,
  FaQrcode,
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaSpinner,
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaCamera,
  FaKeyboard,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// =========================================================
// SEARCHABLE SELECT
// =========================================================
const SearchableSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder = "Select option",
  disabled = false,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const getOptionValue = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") return opt.value ?? opt.id ?? "";
    return opt;
  };

  const getOptionLabel = (opt) => {
    if (opt === null || opt === undefined) return "";
    if (typeof opt === "object") {
      return (
        opt.label ??
        opt.name ??
        opt.title ??
        String(opt.value ?? opt.id ?? "")
      );
    }
    return String(opt);
  };

  const filteredOptions = options.filter((opt) =>
    getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (option) => {
    const rawVal = getOptionValue(option);
    const safeVal =
      rawVal === null || rawVal === undefined ? "" : String(rawVal);
    onChange({ target: { name, value: safeVal, selected: option } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => !prev);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: "" } });
    setSearchTerm("");
  };

  const selectedOption = options.find(
    (opt) => String(getOptionValue(opt)) === String(value)
  );
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : String(value || "");

  return (
    <div className={classes.searchableSelect} ref={wrapperRef}>
      <div
        className={`${classes.selectTrigger} ${
          isOpen ? classes.selectTriggerOpen : ""
        } ${disabled || loading ? classes.selectDisabled : ""}`}
        onClick={handleToggle}
      >
        <span
          className={
            displayLabel ? classes.selectValue : classes.selectPlaceholder
          }
        >
          {loading ? "Loading..." : displayLabel || placeholder}
        </span>
        <div className={classes.selectIcons}>
          {value && !disabled && !loading && (
            <button
              type="button"
              className={classes.clearBtn}
              onClick={handleClear}
              tabIndex={-1}
            >
              <FaTimes />
            </button>
          )}
          <FaChevronDown
            className={`${classes.chevronIcon} ${
              isOpen ? classes.chevronRotate : ""
            }`}
          />
        </div>
      </div>

      {isOpen && !disabled && !loading && (
        <div className={classes.selectDropdown}>
          <div className={classes.searchBox}>
            <FaSearch className={classes.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search patient by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchInput}
              onClick={(e) => e.stopPropagation()}
            />
            {searchTerm && (
              <button
                type="button"
                className={classes.clearSearchBtn}
                onClick={() => setSearchTerm("")}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className={classes.optionsList}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt, i) => {
                const optValue = getOptionValue(opt);
                const optLabel = getOptionLabel(opt);
                return (
                  <div
                    key={`${optValue}-${i}`}
                    className={`${classes.optionItem} ${
                      String(optValue) === String(value)
                        ? classes.optionSelected
                        : ""
                    }`}
                    onClick={() => handleSelect(opt)}
                  >
                    {optLabel}
                  </div>
                );
              })
            ) : (
              <div className={classes.noOptions}>No patients found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================
// SCAN ID MODAL
// =========================================================
const ScanIdModal = ({ open, onClose, onScanned, isScanning }) => {
  const [manualValue, setManualValue] = useState("");
  const inputRef = useRef(null);
  const manualRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  useEffect(() => {
    if (!open) setManualValue("");
  }, [open]);

  const handleScannerKey = (e) => {
    if (e.key === "Enter") {
      const code = e.target.value.trim();
      if (code) {
        onScanned(code);
        e.target.value = "";
      }
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const code = manualValue.trim();
    if (code) onScanned(code);
  };

  if (!open) return null;

  return (
    <div className={classes.modalOverlay} onClick={onClose}>
      <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
        <div className={classes.modalHeader}>
          <h3>
            <FaQrcode /> Scan Patient ID
          </h3>
          <button className={classes.modalClose} onClick={onClose} type="button">
            <FaTimes />
          </button>
        </div>

        <div className={classes.modalBody}>
          <input
            ref={inputRef}
            type="text"
            className={classes.hiddenScannerInput}
            onKeyDown={handleScannerKey}
            autoComplete="off"
            aria-label="Scanner input"
          />

          <div className={classes.scannerVisual}>
            {isScanning ? (
              <>
                <FaSpinner className={classes.spinLarge} />
                <p>Searching patient...</p>
              </>
            ) : (
              <>
                <FaCamera className={classes.scannerIcon} />
                <p className={classes.scannerTitle}>Ready to scan</p>
                <span className={classes.scannerHint}>
                  Point the barcode / QR scanner at the patient's ID card.
                  It will fill automatically.
                </span>
              </>
            )}
          </div>

          <div className={classes.divider}>
            <span>or enter manually</span>
          </div>

          <form onSubmit={handleManualSubmit} className={classes.manualForm}>
            <div className={classes.inputWithIcon}>
              <FaKeyboard className={classes.inputIcon} />
              <input
                ref={manualRef}
                type="text"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                placeholder="Enter Patient ID / UHID / MRN"
                disabled={isScanning}
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              className={classes.manualBtn}
              disabled={isScanning || !manualValue.trim()}
            >
              {isScanning ? (
                <>
                  <FaSpinner className={classes.spin} /> Looking up...
                </>
              ) : (
                "Look Up"
              )}
            </button>
          </form>
        </div>

        <div className={classes.modalFooter}>
          <button className={classes.modalCancel} onClick={onClose} type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const Reception = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ---- Patient selection ----
  const [registeredPatient, setRegisteredPatient] = useState(null);
  const [patients, setPatients] = useState([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState("");

  const [patientForm, setPatientForm] = useState({
    full_name: "",
    reason_for_visit: "",
  });

  // ---- Consultation types ----
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [isLoadingConsultationTypes, setIsLoadingConsultationTypes] =
    useState(false);

  const [consultationForm, setConsultationForm] = useState({
    consultation_type: "",
    consultation_fee: 0,
    service_charge: 0,
  });

  // ---- Nursing assignment ----
  const [nurses, setNurses] = useState([]);
  const [isLoadingNurses, setIsLoadingNurses] = useState(false);

  const [nursingForm, setNursingForm] = useState({
    nurse_id: "",
    priority: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0, 5),
    notes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- Scan ID modal ----
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const priorities = [
    { value: "low", label: "Low Priority" },
    { value: "medium", label: "Medium Priority" },
    { value: "high", label: "High Priority" },
    { value: "critical", label: "Critical" },
  ];

  // ---------------------------------------------------------------
  // FETCH EVERYTHING ON MOUNT
  // ---------------------------------------------------------------
  useEffect(() => {
    fetchNurses();
    fetchPatients();
    fetchConsultationTypes();
  }, []);

  const fetchNurses = async () => {
    setIsLoadingNurses(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(`${BASE_URL}/api/staff`, {
        params: { company_id: companyId, per_page: 500 },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let staffList = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) staffList = response.data.data;
        else if (response.data.data.data) staffList = response.data.data.data;
        else if (response.data.data.staff) staffList = response.data.data.staff;
      }

      const nurseList = staffList
        .filter((s) => {
          const type = String(s.staff_type || "").toLowerCase();
          return type.includes("nurse") || type.includes("midwife");
        })
        .map((n) => {
          const first = n.first_name || "";
          const last = n.last_name || "";
          const name = `${first} ${last}`.trim() || n.employee_no || "Unnamed";
          return { value: n.id, label: name };
        });

      setNurses(nurseList);
    } catch (error) {
      console.error("Error fetching nurses:", error);
      setNurses([]);
    } finally {
      setIsLoadingNurses(false);
    }
  };

  const fetchPatients = async () => {
    setIsLoadingPatients(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(`${BASE_URL}/api/patient`, {
        params: { company_id: companyId, per_page: 500 },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let list = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) list = response.data.data;
        else if (response.data.data.data) list = response.data.data.data;
        else if (response.data.data.patients) list = response.data.data.patients;
        else if (response.data.data.patient) list = [response.data.data.patient];
      } else if (Array.isArray(response.data)) {
        list = response.data;
      }

      const normalized = list.map((p) => {
        const first = p.first_name || "";
        const middle = p.middle_name || "";
        const last = p.last_name || "";
        const name =
          p.name ||
          `${first} ${middle} ${last}`.replace(/\s+/g, " ").trim() ||
          p.full_name ||
          "Unnamed";

        const patientNo =
          p.patient_no || p.uhid || p.patient_number || p.mrn || "";

        return {
          value: p.id,
          label: patientNo ? `${name} — ${patientNo}` : name,
          raw: p,
          full_name: name,
          patient_no: patientNo,
        };
      });

      setPatients(normalized);
    } catch (error) {
      console.error("Error fetching patients:", error);
      setPatients([]);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  const fetchConsultationTypes = async () => {
    setIsLoadingConsultationTypes(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");
      const response = await axios.get(
        `${BASE_URL}/api/consultation_type`,
        {
          params: { company_id: companyId, per_page: 100 },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      let list = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) list = response.data.data;
        else if (response.data.data.data) list = response.data.data.data;
        else if (response.data.data.consultation_types)
          list = response.data.data.consultation_types;
      } else if (Array.isArray(response.data)) {
        list = response.data;
      }

      const options = list.map((t) => ({
        value: t.id,
        label: t.name || "Unnamed",
        fee: parseFloat(t.consultation_fee || 0),
        service_charge: parseFloat(t.service_charge || 0),
        raw: t,
      }));

      setConsultationTypes(options);
    } catch (error) {
      console.error("Error fetching consultation types:", error);
      setConsultationTypes([]);
    } finally {
      setIsLoadingConsultationTypes(false);
    }
  };

  // ---------------------------------------------------------------
  // AUTO-CONFIRM PATIENT WHEN PICKED (NO ALERT)
  // ---------------------------------------------------------------
  const autoFillFromPatient = (patientRecord) => {
    const raw = patientRecord.raw || patientRecord;

    setPatientForm((prev) => ({
      ...prev,
      full_name: patientRecord.full_name || "",
    }));

    setRegisteredPatient({
      id: raw.id || patientRecord.value,
      patient_no:
        patientRecord.patient_no ||
        raw.patient_no ||
        raw.uhid ||
        raw.patient_number ||
        "",
      full_name: patientRecord.full_name,
    });
  };

  const handleSelectPatient = (e) => {
    const patientId = e.target.value;
    setSelectedPatientId(patientId);

    const selected = patients.find(
      (p) => String(p.value) === String(patientId)
    );

    if (!selected) {
      setPatientForm({ full_name: "", reason_for_visit: "" });
      setRegisteredPatient(null);
      return;
    }

    // Just fill the form — no Swal alert
    autoFillFromPatient(selected);
  };

  // ---------------------------------------------------------------
  // SCAN ID HANDLER (silent — no alert on success)
  // ---------------------------------------------------------------
  const handleScannedCode = async (code) => {
    const cleanCode = String(code).trim();
    if (!cleanCode) return;

    setIsScanning(true);

    try {
      const localMatch = patients.find((p) => {
        const raw = p.raw || {};
        const candidates = [
          p.patient_no,
          raw.patient_no,
          raw.uhid,
          raw.patient_number,
          raw.mrn,
          raw.id,
          raw.phone,
          raw.phone_number,
          raw.email,
        ]
          .filter((c) => c !== null && c !== undefined)
          .map((c) => String(c).toLowerCase());

        return candidates.includes(cleanCode.toLowerCase());
      });

      if (localMatch) {
        autoFillFromPatient(localMatch);
        setSelectedPatientId(String(localMatch.value));
        // no alert — silently succeeds
        setScanModalOpen(false);
        setIsScanning(false);
        return;
      }

      const companyId = parseInt(localStorage.getItem("companyId") || "1");

      const response = await axios.get(`${BASE_URL}/api/patient`, {
        params: {
          company_id: companyId,
          search: cleanCode,
          per_page: 10,
        },
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      let results = [];
      if (response.data?.data) {
        if (Array.isArray(response.data.data)) results = response.data.data;
        else if (response.data.data.data) results = response.data.data.data;
        else if (response.data.data.patients)
          results = response.data.data.patients;
        else if (response.data.data.patient)
          results = [response.data.data.patient];
      } else if (Array.isArray(response.data)) {
        results = response.data;
      }

      const exact = results.find((r) => {
        const candidates = [
          r.patient_no,
          r.uhid,
          r.patient_number,
          r.mrn,
          r.id,
          r.phone,
          r.phone_number,
          r.email,
        ]
          .filter((c) => c !== null && c !== undefined)
          .map((c) => String(c).toLowerCase());

        return candidates.includes(cleanCode.toLowerCase());
      });

      const match = exact || results[0];

      if (!match) {
        // Only show alert when nothing is found
        Swal.fire({
          icon: "warning",
          title: "Patient Not Found",
          text: `No patient matches ID "${cleanCode}"`,
          confirmButtonColor: "#2F80ED",
        });
        setIsScanning(false);
        return;
      }

      const first = match.first_name || "";
      const middle = match.middle_name || "";
      const last = match.last_name || "";
      const full_name =
        match.name ||
        `${first} ${middle} ${last}`.replace(/\s+/g, " ").trim() ||
        "Unnamed";
      const patient_no =
        match.patient_no ||
        match.uhid ||
        match.patient_number ||
        match.mrn ||
        "";

      const normalized = {
        value: match.id,
        label: patient_no ? `${full_name} — ${patient_no}` : full_name,
        raw: match,
        full_name,
        patient_no,
      };

      autoFillFromPatient(normalized);
      setSelectedPatientId(String(match.id));
      // no alert — silent success
      setScanModalOpen(false);
    } catch (error) {
      console.error("Scan lookup failed:", error);
      Swal.fire({
        icon: "error",
        title: "Lookup Failed",
        text: "Could not look up patient. Check your connection and try again.",
        confirmButtonColor: "#2F80ED",
      });
    } finally {
      setIsScanning(false);
    }
  };

  // ---------------------------------------------------------------
  // FORM HANDLERS
  // ---------------------------------------------------------------
  const handlePatientChange = (e) => {
    const { name, value } = e.target;
    setPatientForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsultationChange = (e) => {
    const { name, value } = e.target;
    if (name === "consultation_type") {
      const match = consultationTypes.find(
        (t) => String(t.value) === String(value)
      );
      setConsultationForm((prev) => ({
        ...prev,
        consultation_type: value,
        consultation_fee: match ? match.fee : 0,
        service_charge: match ? match.service_charge : 0,
      }));
    } else {
      setConsultationForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNursingChange = (e) => {
    const { name, value } = e.target;
    setNursingForm((prev) => ({ ...prev, [name]: value }));
  };

  const totalAmount =
    Number(consultationForm.consultation_fee || 0) +
    Number(consultationForm.service_charge || 0);

  // ---------------------------------------------------------------
  // SUBMIT →  POST /api/consultation_register/create
  // ---------------------------------------------------------------
  const handleRegisterConsultation = async () => {
    const errors = [];

    if (!registeredPatient?.id) {
      errors.push("Please select a patient.");
    }
    if (!patientForm.reason_for_visit || !patientForm.reason_for_visit.trim()) {
      errors.push("Reason for visit is required.");
    } else if (patientForm.reason_for_visit.length > 500) {
      errors.push("Reason for visit must be 500 characters or less.");
    }
    if (!consultationForm.consultation_type) {
      errors.push("Consultation type is required.");
    }
    if (!nursingForm.nurse_id) {
      errors.push("Nurse is required.");
    }
    if (!nursingForm.priority) {
      errors.push("Priority is required.");
    }
    if (!nursingForm.date) {
      errors.push("Schedule date is required.");
    }
    if (!nursingForm.time) {
      errors.push("Schedule time is required.");
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Please fix the following errors",
        html: `<div style="text-align: left; font-size: 14px;">${errors
          .map((e, i) => `${i + 1}. ${e}`)
          .join("<br/>")}</div>`,
        confirmButtonColor: "#2F80ED",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const companyId = parseInt(localStorage.getItem("companyId") || "1");

      const payload = {
        company_id: companyId,
        patient_id: parseInt(registeredPatient.id),
        reason_for_visit: patientForm.reason_for_visit.trim(),
        consultation_type_id: parseInt(consultationForm.consultation_type),
        nurse_id: parseInt(nursingForm.nurse_id),
        priority: nursingForm.priority,
        schedule_date: nursingForm.date,
        schedule_time: nursingForm.time,
        notes: nursingForm.notes?.trim() || null,
      };

      const response = await axios.post(
        `${BASE_URL}/api/consultation_register/create`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Consultation registered successfully!",
        timer: 1800,
        showConfirmButton: false,
        timerProgressBar: true,
      });

      setRegisteredPatient(null);
      setSelectedPatientId("");
      setPatientForm({ full_name: "", reason_for_visit: "" });
      setConsultationForm({
        consultation_type: "",
        consultation_fee: 0,
        service_charge: 0,
      });
      setNursingForm({
        nurse_id: "",
        priority: "",
        date: new Date().toISOString().split("T")[0],
        time: new Date().toTimeString().slice(0, 5),
        notes: "",
      });
    } catch (error) {
      console.error("Error registering consultation:", error);
      let msg = "Failed to register consultation.";

      if (error.response?.data?.errors) {
        const errs = error.response.data.errors;
        const firstKey = Object.keys(errs)[0];
        msg = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : msg;
      } else if (error.response?.data?.message) {
        const m = error.response.data.message;
        if (typeof m === "object") {
          const firstKey = Object.keys(m)[0];
          msg = Array.isArray(m[firstKey]) ? m[firstKey][0] : JSON.stringify(m);
        } else {
          msg = m;
        }
      } else if (error.request) {
        msg = "Unable to connect to server.";
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: msg,
        confirmButtonColor: "#2F80ED",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={classes.main}>
        <Header toggleSidebar={toggleSidebar} />
        <div className={classes.content}>
          {/* Page header */}
          <div className={classes.pageHeader}>
            <div className={classes.headerLeft}>
              <div className={classes.headerIcon}>
                <FaUserCheck />
              </div>
              <div>
                <h2>Consultation</h2>
                <p>
                  Select a patient, generate a consultation bill, and schedule
                  for nursing
                </p>
              </div>
            </div>
          </div>

          {/* ================= PATIENT INFORMATION ================= */}
          <div className={classes.card}>
            <div className={classes.cardHeader}>
              <h3>Patient Information</h3>
              <button
                className={classes.scanBtn}
                type="button"
                onClick={() => setScanModalOpen(true)}
              >
                <FaQrcode /> Scan ID
              </button>
            </div>

            <div className={classes.field}>
              <label>Full Name</label>
              <SearchableSelect
                name="patient_id"
                value={selectedPatientId}
                onChange={handleSelectPatient}
                options={patients}
                placeholder={
                  isLoadingPatients
                    ? "Loading patients..."
                    : "Search patient by name or ID..."
                }
                loading={isLoadingPatients}
                disabled={isLoadingPatients}
              />
            </div>

            <div className={classes.field}>
              <label>
                Reason for Visit / Complaint <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <textarea
                name="reason_for_visit"
                value={patientForm.reason_for_visit}
                onChange={handlePatientChange}
                placeholder="State reason for visit (max 500 characters)"
                rows="3"
                maxLength={500}
              />
            </div>
          </div>

          {/* ================= TWO COLUMN ================= */}
          <div className={classes.twoCol}>
            {/* ---------- CONSULTATION BILL ---------- */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <h3>Consultation Bill</h3>
              </div>

              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Patient Name</span>
                <span className={classes.infoValue}>
                  {registeredPatient?.full_name || "—"}
                </span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Patient ID</span>
                <span className={classes.infoValue}>
                  {registeredPatient?.patient_no || "—"}
                </span>
              </div>

              <div className={classes.field} style={{ marginTop: 12 }}>
                <label>
                  Consultation Type <span style={{ color: "#EF4444" }}>*</span>
                </label>
                <select
                  name="consultation_type"
                  value={consultationForm.consultation_type}
                  onChange={handleConsultationChange}
                  disabled={isLoadingConsultationTypes}
                >
                  <option value="">
                    {isLoadingConsultationTypes
                      ? "Loading consultation types..."
                      : "Select consultation type"}
                  </option>
                  {consultationTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <table className={classes.miniTable}>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Unit Price (₦)</th>
                    <th>Total (₦)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Consultation Fee</td>
                    <td>
                      {Number(
                        consultationForm.consultation_fee || 0
                      ).toLocaleString()}
                    </td>
                    <td>
                      {Number(
                        consultationForm.consultation_fee || 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>Service Charge</td>
                    <td>
                      {Number(
                        consultationForm.service_charge || 0
                      ).toLocaleString()}
                    </td>
                    <td>
                      {Number(
                        consultationForm.service_charge || 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className={classes.totalRow}>
                <span>Total Amount</span>
                <strong>₦{totalAmount.toLocaleString()}</strong>
              </div>
            </div>

            {/* ---------- SCHEDULE FOR NURSING ---------- */}
            <div className={classes.card}>
              <div className={classes.cardHeader}>
                <h3>Schedule for Nursing</h3>
              </div>

              {registeredPatient ? (
                <div className={classes.successBox}>
                  <FaCheckCircle />
                  <div>
                    <strong>Patient ready for consultation!</strong>
                    <p>Complete the form and submit.</p>
                  </div>
                </div>
              ) : (
                <div className={classes.infoBox}>
                  Select a patient to enable nursing assignment.
                </div>
              )}

              <p className={classes.subHeading}>
                Assign to Nursing for Vital Signs &amp; Compliant Recording
              </p>

              <div className={classes.row2}>
                <div className={classes.field}>
                  <label>
                    Nurse <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <select
                    name="nurse_id"
                    value={nursingForm.nurse_id}
                    onChange={handleNursingChange}
                    disabled={isLoadingNurses}
                  >
                    <option value="">
                      {isLoadingNurses ? "Loading nurses..." : "Select Nurse"}
                    </option>
                    {nurses.map((n) => (
                      <option key={n.value} value={n.value}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={classes.field}>
                  <label>
                    Priority <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <select
                    name="priority"
                    value={nursingForm.priority}
                    onChange={handleNursingChange}
                  >
                    <option value="">Select Priority Level</option>
                    {priorities.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={classes.row2}>
                <div className={classes.field}>
                  <label>
                    Date <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={nursingForm.date}
                    onChange={handleNursingChange}
                  />
                </div>
                <div className={classes.field}>
                  <label>
                    Time <span style={{ color: "#EF4444" }}>*</span>
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={nursingForm.time}
                    onChange={handleNursingChange}
                  />
                </div>
              </div>

              <div className={classes.field}>
                <label>Notes (Optional)</label>
                <textarea
                  name="notes"
                  value={nursingForm.notes}
                  onChange={handleNursingChange}
                  placeholder="Any special instructions..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          {/* ================= SUBMIT BUTTON ================= */}
          <div className={classes.card}>
            <button
              type="button"
              className={classes.primaryBtnFull}
              onClick={handleRegisterConsultation}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className={classes.spin} /> Registering
                  Consultation...
                </>
              ) : (
                <>
                  <FaFileInvoiceDollar /> Register Consultation
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ================= SCAN ID MODAL ================= */}
      <ScanIdModal
        open={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onScanned={handleScannedCode}
        isScanning={isScanning}
      />
    </div>
  );
};

export default Reception;